import type React from 'react';

import { Button, Text } from '@change/ds-core-ui';
import { TextField } from '@change/ds-forms';

import { Translate, useI18n } from '@change-corgi/core/react/i18n';

import { photoPlaceholder, photoUploadControls, photoUploadLabel, photoUploadSection } from '../../shared/styles';
import type { EndorsementFormData } from '../../shared/types';

type PhotoUploadSectionProps = {
	photoUrl: string | undefined;
	onPhotoUpload: () => void;
};

const PhotoUploadSection: React.FC<PhotoUploadSectionProps> = ({ photoUrl, onPhotoUpload }) => {
	return (
		<div className={photoUploadSection()}>
			<div className={photoUploadLabel()}>
				<Text variant="body" fontWeight="bold" className="text-typography-strong">
					<Translate value="corgi.petition.starter_dashboard_v2.endorsements.add.photo_label" />
				</Text>
				<Text variant="body" size="x-small" className="text-typography-weak">
					<Translate value="corgi.petition.starter_dashboard_v2.endorsements.add.photo_helper" />
				</Text>
			</div>

			<div className={photoUploadControls()}>
				<div className={photoPlaceholder()} data-testid="photo-placeholder">
					{photoUrl ? (
						<img
							src={photoUrl}
							alt=""
							className="h-full w-full rounded-full object-cover"
							data-testid="uploaded-photo"
						/>
					) : (
						<svg xmlns="http://www.w3.org/2000/svg" width="39" height="39" viewBox="0 0 39 39" fill="none">
							<path
								d="M30.3998 7.99999V30.4H7.9998V7.99999H30.3998ZM30.3998 4.79999H7.9998C6.2398 4.79999 4.7998 6.23999 4.7998 7.99999V30.4C4.7998 32.16 6.2398 33.6 7.9998 33.6H30.3998C32.1598 33.6 33.5998 32.16 33.5998 30.4V7.99999C33.5998 6.23999 32.1598 4.79999 30.3998 4.79999ZM22.6238 18.976L17.8238 25.168L14.3998 21.024L9.5998 27.2H28.7998L22.6238 18.976Z"
								fill="#766F81"
							/>
						</svg>
					)}
				</div>

				<Button
					variant="secondary-solid"
					size="small"
					onClick={onPhotoUpload}
					data-testid="upload-photo-button"
					data-qa="upload-photo-button"
				>
					<Translate value="corgi.petition.starter_dashboard_v2.endorsements.add.upload_photo_button" />
				</Button>
			</div>
		</div>
	);
};

type EndorsementFormFieldsProps = {
	formData: EndorsementFormData;
	statementCharCount: number;
	statementMaxLength: number;
	onFieldChange: (field: keyof EndorsementFormData, value: string) => void;
	onPhotoUpload: () => void;
};

export const EndorsementFormFields: React.FC<EndorsementFormFieldsProps> = ({
	formData,
	statementCharCount,
	statementMaxLength,
	onFieldChange,
	onPhotoUpload,
}) => {
	const { translate } = useI18n();

	return (
		<>
			{/* Name Field */}
			<TextField
				name="endorserName"
				label={translate('corgi.petition.starter_dashboard_v2.endorsements.add.name_label')}
				supportingText={translate('corgi.petition.starter_dashboard_v2.endorsements.add.name_helper')}
				value={formData.name || ''}
				onChange={(value) => onFieldChange('name', value)}
				data-testid="endorser-name-field"
				data-qa="endorser-name-field"
				size="large"
				boldLabel
			/>

			{/* Title/Description Field */}
			<TextField
				name="endorserTitle"
				label={translate('corgi.petition.starter_dashboard_v2.endorsements.add.title_label')}
				supportingText={translate('corgi.petition.starter_dashboard_v2.endorsements.add.title_helper')}
				value={formData.title || ''}
				onChange={(value) => onFieldChange('title', value)}
				data-testid="endorser-title-field"
				data-qa="endorser-title-field"
				size="large"
				boldLabel
			/>

			{/* Statement Field (Optional) */}
			<div className="flex flex-col gap-1">
				<TextField
					name="endorserStatement"
					inputElementType="textarea"
					label={`${translate('corgi.petition.starter_dashboard_v2.endorsements.add.statement_label')} ${translate('corgi.petition.starter_dashboard_v2.endorsements.add.optional_label')}`}
					supportingText={translate('corgi.petition.starter_dashboard_v2.endorsements.add.statement_helper')}
					value={formData.statement || ''}
					onChange={(value) => onFieldChange('statement', value)}
					inputOptions={{
						maxLength: statementMaxLength,
						className: 'min-h-28',
					}}
					data-testid="endorser-statement-field"
					data-qa="endorser-statement-field"
					size="large"
					boldLabel
				/>
				<div className="flex items-center justify-end gap-6">
					<Text variant="body" size="x-small" className="text-typography-weak text-right">
						{statementCharCount}/{statementMaxLength}
					</Text>
				</div>
			</div>

			{/* Photo Upload Section */}
			<PhotoUploadSection photoUrl={formData.photoUrl} onPhotoUpload={onPhotoUpload} />

			{/* URL Field */}
			<TextField
				name="endorserUrl"
				label={translate('corgi.petition.starter_dashboard_v2.endorsements.add.url_label')}
				supportingText={translate('corgi.petition.starter_dashboard_v2.endorsements.add.url_helper')}
				value={formData.url || ''}
				onChange={(value) => onFieldChange('url', value)}
				data-testid="endorser-url-field"
				data-qa="endorser-url-field"
				size="large"
				boldLabel
			/>
		</>
	);
};
